import { Elements } from '@stripe/react-stripe-js';
import {
  FaAward,
  FaCity,
  FaCreditCard,
  FaEnvelope,
  FaHome,
  FaMapMarkerAlt,
  FaMapPin,
  FaPhoneAlt,
  FaRegBuilding,
  FaRegMap,
  FaRegUser,
  FaUser
} from 'react-icons/fa';
import PaymentForm from './StripePaymentForm';
import {
  StepWrapper,
  VegasBackground,
  VegasHeader,
  VegasStepper,
  VegasCard,
  SectionHeader,
  SectionDivider,
  IconInput,
  IconSelect,
  VegasFooterButtons,
  SummaryItem,
  US_STATE_OPTIONS
} from 'components/VegasConference';

const plateStyles = {
  Bronze: {
    border: 'border-[#cd7f32]',
    badge: 'from-[#f6e3c2] to-[#cd7f32]'
  },
  Silver: {
    border: 'border-[#c0c0c0]',
    badge: 'from-[#e5e7eb] to-[#9ca3af]'
  },
  Gold: {
    border: 'border-[#ffd700]',
    badge: 'from-[#ffd700] to-[#f59e0b]'
  },
  Platinum: {
    border: 'border-[#7dd3fc]',
    badge: 'from-[#7dd3fc] to-[#38bdf8]'
  }
};

const personalFields = [
  { key: 'name', label: 'Full Name', icon: FaRegUser },
  { key: 'company', label: 'Company', icon: FaRegBuilding },
  { key: 'phone', label: 'Phone', icon: FaPhoneAlt, type: 'tel' },
  { key: 'email', label: 'Email', icon: FaEnvelope, type: 'email' }
];

const billingFields = [
  { key: 'address', label: 'Address', icon: FaHome },
  { key: 'city', label: 'City', icon: FaCity },
  { key: 'state', label: 'State', icon: FaRegMap, options: US_STATE_OPTIONS },
  { key: 'zip', label: 'Zip Code', icon: FaMapPin }
];

const Body = ({ _this }) => {
  const {
    step,
    setStep,
    form,
    setForm,
    plans,
    selectedPlan,
    setSelectedPlan,
    clientSecret,
    stripePromise,
    validateStep1,
    validateStep2
  } = _this;

  const isStep1Valid = validateStep1();
  const isStep2Valid = validateStep2();

  const tier = selectedPlan?.name?.split(' ')[0];
  const badgeStyle = tier ? plateStyles[tier] : null;

  const renderField = (f) => {
    const props = {
      icon: f.icon,
      label: f.label,
      required: true,
      value: form[f.key],
      hasError: !form[f.key] && !isStep1Valid,
      onChange: (value) => setForm({ ...form, [f.key]: value })
    };

    return f.options ? (
      <IconSelect key={f.key} {...props} options={f.options} />
    ) : (
      <IconInput key={f.key} {...props} type={f.type} />
    );
  };

  return (
    <VegasBackground>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12">
        <VegasHeader
          title="VEGAS CONFERENCE 2027"
          tagline="THE POWER OF M.E."
          subTagline="ONE TEAM. ONE MISSION."
        />

        <VegasStepper currentStep={step} steps={['Details', 'Plan', 'Payment']} />

        {/* STEP 1 */}
        {step === 1 && (
          <StepWrapper>
            <VegasCard>
              <SectionHeader
                icon={FaUser}
                title="Personal Information"
                subtitle="Tell us a little about yourself."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personalFields.map(renderField)}
              </div>

              <SectionDivider />

              <SectionHeader
                icon={FaMapMarkerAlt}
                title="Billing Details"
                subtitle="Enter your billing address."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {billingFields.map(renderField)}
              </div>
            </VegasCard>
          </StepWrapper>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <StepWrapper>
            <VegasCard className="!max-w-7xl">
              <SectionHeader
                icon={FaAward}
                title="Choose Your Sponsorship Level"
                subtitle="Select the plan that fits your business."
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {plans.map((p) => {
                  const tier = p.name.split(' ')[0];
                  const style = plateStyles[tier];
                  const isActive = selectedPlan?.name === p.name;

                  return (
                    <div
                      key={p.name}
                      onClick={() => setSelectedPlan(p)}
                      className={`
                        bg-white text-slate-800 p-6 border-2 ${style.border}
                        rounded-2xl transition-all duration-300
                        hover:-translate-y-2 hover:shadow-xl
                        flex flex-col cursor-pointer
                        ${isActive ? 'ring-2 ring-[#13235f] scale-[1.03] shadow-xl' : ''}
                      `}
                    >
                      <div>
                        <div className="flex justify-between items-start mb-4 gap-2">
                          <h3 className="text-lg font-bold text-[#13235f] tracking-wide">
                            {p.name}
                          </h3>

                          <span
                            className={`px-4 py-1 rounded-full text-sm font-bold bg-gradient-to-r ${style.badge} text-white shadow-sm`}
                          >
                            ${p.price.toLocaleString()}
                          </span>
                        </div>

                        <div className="h-px bg-slate-200 my-3" />

                        <ul className="text-left space-y-2 mt-3 text-slate-600 text-sm mb-4 pl-0">
                          {p.description.map((d, i) => (
                            <li key={i} className="flex gap-2 items-start">
                              <span className="text-emerald-500 mt-[2px]">✔</span>
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button
                        type="button"
                        className={`mt-auto w-full h-11 rounded-xl font-semibold transition shadow-md
                          ${
                            isActive
                              ? 'bg-emerald-500 text-white'
                              : 'bg-[#13235f] text-white hover:bg-[#0d1a4f]'
                          }`}
                      >
                        {isActive ? 'Selected ✓' : 'Select Plan →'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </VegasCard>
          </StepWrapper>
        )}

        {/* STEP 3 */}
        {step === 3 && selectedPlan && (
          <StepWrapper>
            <VegasCard>
              <SectionHeader
                icon={FaCreditCard}
                title="Payment"
                subtitle="Review your details and complete your payment."
              />

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 mb-6">
                <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
                  <h6 className="m-0 text-sm font-semibold text-[#13235f]">Billing Summary</h6>
                  {badgeStyle && (
                    <p
                      className={`m-0 w-fit px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r ${badgeStyle.badge} text-white shadow-sm whitespace-nowrap`}
                    >
                      {selectedPlan.name} · ${selectedPlan.price.toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <SummaryItem label="Name" value={form.name} />
                  <SummaryItem label="Company" value={form.company} />
                  <SummaryItem label="Email" value={form.email} />
                  <SummaryItem label="Phone" value={form.phone} />
                  <SummaryItem
                    label="Address"
                    value={`${form.address}, ${form.city}, ${form.state} ${form.zip}`}
                  />
                </div>
              </div>

              {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <PaymentForm setStep={setStep} setForm={setForm} />
                </Elements>
              )}
            </VegasCard>
          </StepWrapper>
        )}

        <VegasFooterButtons
          showBack={step > 1}
          showContinue={step < 3}
          disabled={(step === 1 && !isStep1Valid) || (step === 2 && !isStep2Valid)}
          onBack={() => setStep(step - 1)}
          onContinue={() => setStep(step + 1)}
        />
      </div>
    </VegasBackground>
  );
};

export default Body;
